import {
  DependentSimpleData,
  ISpecialtyResponse,
  IUser,
  IdentificadorClinica,
} from '@/modules/Auth/auth-types'
import { emailValidator } from '@/utils/validations'
import { IFirebaseAuthentication } from '@/modules/Firebase/firebase-types'
import { capitalize } from '@/utils/fnUtils'
export interface IAddress {
  cep?: string
  formattedCep?: string
  streetName?: string
  number?: string
  complement?: string
  state?: string
  city?: string
  neighborhood?: string
  country?: string
}
export interface IEmergencyContact {
  emergencyContactFullName?: string
  relationship?: string
  emergencyContactCellphone?: string
  emergencyContactId?: number | null
}

export interface PregnantInformation {
  weeksPregnancy?: string
  daysPregnancy?: string
  lastMenstrualPeriodDate?: string
}
export class User {
  private readonly _name: string
  constructor(
    public pregnantInformation: PregnantInformation,
    public contactsEmergency: IEmergencyContact,
    private _id: number,
    private _cpf: string,
    private _patientId: number,
    _name: string,
    private _patientName: string,
    public photo: string | null,
    private _origin: string | null,
    private _redirectUrl: string | null,
    public _token: string,
    public _frontCnx: boolean,
    public _refreshToken: string | null,
    private _clinic: { id: number; name: string; product: string },
    public birthdate: string | null,
    public age: number | null,
    public sex: string | null,
    public cellphone: string | null,
    public parsedCellphone: string | null,
    public email: string | null,
    public login: string,
    public ip: string | null,
    public address: IAddress,
    public firebaseToken: string,
    private _firebaseAuthentication: IFirebaseAuthentication,
    private _isAllowedToNewApplication: boolean,
    public disableHub: boolean,
    private _isAcceptedBenefitTerm: boolean,
    public passportExpirationDate: string | null,
    public dateIssuePassport: string | null,
    public passport: string | null,
    public idCountry: string | null,
    public healthInsuranceNumber: string | null,
    public issuingBodyRg: string | null,
    public dateIssueRg: string | null,
    public rg: string | null,
    public ethnicity: string | null,
    public idGenre: string | null,
    public idNacionality: string | null,
    public idRaceOrColor: string | null,
    public nameMother: string | null,
    public unknownMother: boolean | null,
    public socialName: string | null,
    public cns: string | null,
    public birthCity: string | null,
    public idUfBirth: number | null,
    public isHealthTeam: boolean, // public allowLogin: boolean, // public directAccessLink?: string, // public androidAppLink?: string, // public iosAppLink?: string,
    public holder: string | null,
    public needToUpdateAccountData: boolean,
    public flagAccessedBefore: boolean | null,
    public isMinorPatient: boolean,
    public responsable: {
      id: number | null
      name: string | null
    } | null,
    public clinicSpecialites: {
      socialWorker: ISpecialtyResponse
      dentist: ISpecialtyResponse
      nursing: ISpecialtyResponse
      physiotherapy: ISpecialtyResponse
      speechTherapist: ISpecialtyResponse
      medic: ISpecialtyResponse
      nutrition: ISpecialtyResponse
      psychology: ISpecialtyResponse
      psyAlive: ISpecialtyResponse
    },
    public flagIsActive: boolean,
    public adultDependents: DependentSimpleData[],
    public childrenDependents: DependentSimpleData[],
    public termUrl: string,
    public clinicIdentifier: IdentificadorClinica,
    public flagAllowBrazeSend: boolean | null,
    public flagTermoAceito: boolean | null,
    public flagPrimeiroAcessoAposTermo: boolean | null,
    public dataHoraTermoAceito: string | null,
    public exibeLogin: boolean | null,
  ) {
    this._name = _name
  }

  get id(): number {
    return this._id
  }

  get origin(): string {
    return this._origin
  }

  get redirectUrl(): string {
    return this._redirectUrl
  }

  get cpf(): string {
    return this._cpf
  }

  get patientId(): number {
    return this._patientId
  }

  get patientName(): string {
    return this._patientName
  }

  get token(): string {
    return this._token
  }

  get frontCnx(): boolean {
    return this._frontCnx
  }

  get refreshToken(): string {
    return this._refreshToken
  }

  get name(): string {
    return this._name !== this._name?.toLowerCase() &&
      this._name !== this._name?.toUpperCase()
      ? this._name
      : capitalize(this._name?.toLowerCase())
  }

  get socialNameOrName(): string {
    return this.socialName ? this.socialName : this._name
  }

  get shortName(): string {
    if (this.socialName) {
      const arrSocialName = this.socialName.split(' ')
      if (arrSocialName.length === 1) return arrSocialName[0]
      if (arrSocialName[1].length > 3)
        return capitalize(arrSocialName.slice(0, 2).join(' '))
      else
        return capitalize(
          arrSocialName[0] + ' ' + arrSocialName[arrSocialName.length - 1],
        )
    } else {
      const arrName = this.name.split(' ')
      if (arrName.length === 1) return arrName[0]
      if (arrName[1].length > 3) return arrName.slice(0, 2).join(' ')
      else return arrName[0] + ' ' + arrName[arrName.length - 1]
    }
  }

  get clinic(): { id: number; name: string; product: string } {
    return this._clinic
  }

  get haveDataPendence(): boolean {
    return (
      !emailValidator(this.email) ||
      !['F', 'M', 'OUTRO'].includes(this.sex) ||
      this.birthdate === null ||
      !this.cellphone ||
      !this.birthdate ||
      this.birthdate.length !== 10
    )
  }

  get firebaseAuthentication(): IFirebaseAuthentication {
    return this._firebaseAuthentication
  }

  get isAllowedToNewApplication(): boolean {
    return this._isAllowedToNewApplication
  }

  get isAcceptedBenefitTerm(): boolean {
    return this._isAcceptedBenefitTerm
  }

  get firstName(): string {
    return this.name?.split(' ')?.[0]
  }

  get lastName(): string {
    return this.name?.split(' ')?.[1]
  }

  public buildUserFromClass(): IUser {
    return {
      informacaoGestante: {
        dias: this.pregnantInformation?.daysPregnancy,
        semanas: this.pregnantInformation?.weeksPregnancy,
        dataUltimaMenstruacao:
          this.pregnantInformation?.lastMenstrualPeriodDate,
      },
      contatoEmergencia: {
        nomeCompleto: this.contactsEmergency.emergencyContactFullName,
        telefone: this.contactsEmergency.emergencyContactCellphone,
        relacao: this.contactsEmergency.relationship,
        idContatoEmergencia: this?.contactsEmergency?.emergencyContactId,
      },
      token: this.token,
      frontCnx: this.frontCnx,
      ip: this.ip,
      tokenFirebase: this.firebaseToken,
      firebaseAuthentication: this._firebaseAuthentication,
      usuario: {
        id: this.id,
        nome: this.name,
        login: this.login,
        urlFoto: this.photo,
        origin: this.origin,
        clinica: {
          id: this.clinic.id,
          nome: this.clinic.name,
          produto: this.clinic.product,
        },
        flagAcessou: this.flagAccessedBefore,
        flagAtivo: this.flagIsActive,
        paciente: {
          id: this.patientId,
          nome: this.name,
          cpf: this.cpf,
          dataNascimento: this.birthdate,
          dataValidadePassaporte: this.passportExpirationDate,
          dataEmissaoPassaporte: this.dateIssuePassport,
          idPaisNascimentoEstrangeiro: this.idCountry,
          orgaoEmissorIdentidade: this.issuingBodyRg,
          dataEmissaoIdentidade: this.dateIssueRg,
          identidade: this.rg,
          etnia: this.ethnicity,
          idGenero: this.idGenre,
          idNacionalidade: this.idNacionality,
          idRacaCor: this.idRaceOrColor,
          nomeMae: this.nameMother,
          flagMaeDesconhecida: this.unknownMother,
          nomeSocial: this.socialName,
          cns: this.cns,
          municipioNascimento: this.birthCity,
          idUfNascimento: this.idUfBirth,
          passaporte: this.passport,
          telefoneCelular: this.cellphone,
          telefoneCelularFormatado: this.parsedCellphone,
          sexo: this.sex,
          email: this.email,
          cep: this.address.cep,
          cepFormatado: this.address.formattedCep,
          bairro: this.address.neighborhood,
          cidade: this.address.city,
          complemento: this.address.complement,
          endereco: this.address.streetName,
          numeroLogradouro: this.address.number,
          carteiraConvenio: this.healthInsuranceNumber,
          pais: this.address.country,
          uf: this.address.state,
          flagAps: this.isHealthTeam,
          titularidade: this.holder,
          menorDeIdade: this?.isMinorPatient,
        },
      },
      servicosClinica: {
        servicoAssistenteSocial: this.clinicSpecialites.socialWorker,
        servicoDentista: this.clinicSpecialites.dentist,
        servicoEnfermagem: this.clinicSpecialites.nursing,
        servicoFisioterapeuta: this.clinicSpecialites.physiotherapy,
        servicoFonoaudiologo: this.clinicSpecialites.speechTherapist,
        servicoMedico: this.clinicSpecialites.medic,
        servicoNutricao: this.clinicSpecialites.nutrition,
        servicoPsicologia: this.clinicSpecialites.psychology,
        servicoPsicologiaViva: this.clinicSpecialites.psyAlive,
      },
      flagNovaExperiencia: this.isAllowedToNewApplication,
      flagExisteBeneficioAceito: this._isAcceptedBenefitTerm,
      flagDesabilitarHub: this.disableHub,
      dependentesMaiorIdade: this.adultDependents,
      dependentesMenorIdade: this.childrenDependents,
      termoUrl: this.termUrl,
      identificadorAtivacao: this.clinicIdentifier,
      flagPermiteEnvioBraze: this.flagAllowBrazeSend,
    }
  }
}
export const buildUserFromInterface = (user: IUser): User => {
  return new User(
    {
      daysPregnancy: user?.informacaoGestante?.dias,
      weeksPregnancy: user?.informacaoGestante?.semanas,
      lastMenstrualPeriodDate: user?.informacaoGestante?.dataUltimaMenstruacao,
    },
    {
      emergencyContactFullName: user?.contatoEmergencia?.nomeCompleto,
      relationship: user?.contatoEmergencia?.relacao,
      emergencyContactCellphone: user?.contatoEmergencia?.telefone,
      emergencyContactId: user?.contatoEmergencia?.idContatoEmergencia,
    },
    user?.usuario?.id,
    user?.usuario?.paciente?.cpf,
    user?.usuario?.paciente?.id,
    user?.usuario?.nome,
    user?.usuario?.paciente?.nome,
    user?.usuario?.urlFoto,
    user?.usuario?.origin,
    user?.usuario?.urlRedirecionamento,
    user?.token,
    user?.frontCnx,
    user?.refreshToken,
    {
      id: user?.usuario?.clinica?.id,
      name: user?.usuario?.clinica?.nome,
      product: user?.usuario.clinica?.produto,
    },
    user?.usuario?.paciente?.dataNascimento,
    user?.usuario?.paciente?.idade,
    user?.usuario?.paciente?.sexo,
    user?.usuario?.paciente?.telefoneCelular,
    user?.usuario?.paciente?.telefoneCelularFormatado,
    user?.usuario?.paciente?.email,
    user?.usuario?.login,
    user?.ip,
    {
      cep: user?.usuario?.paciente?.cep,
      formattedCep: user?.usuario?.paciente?.cepFormatado,
      streetName: user?.usuario?.paciente?.endereco,
      number: user?.usuario?.paciente?.numeroLogradouro || '',
      complement: user?.usuario?.paciente?.complemento,
      neighborhood: user?.usuario?.paciente?.bairro,
      state: user?.usuario?.paciente?.uf,
      city: user?.usuario?.paciente?.cidade,
      country: user?.usuario?.paciente?.pais,
    },
    user?.tokenFirebase,
    user?.firebaseAuthentication,
    user?.flagNovaExperiencia,
    user?.flagDesabilitarHub,
    user?.flagExisteBeneficioAceito,
    user?.usuario?.paciente?.dataValidadePassaporte,
    user?.usuario?.paciente?.dataEmissaoPassaporte,
    user?.usuario?.paciente?.passaporte,
    user?.usuario?.paciente?.idPaisNascimentoEstrangeiro,
    user?.usuario?.paciente?.carteiraConvenio,
    user?.usuario?.paciente?.orgaoEmissorIdentidade,
    user?.usuario?.paciente?.dataEmissaoIdentidade,
    user?.usuario?.paciente?.identidade,
    user?.usuario?.paciente?.etnia,
    user?.usuario?.paciente?.idGenero,
    user?.usuario?.paciente?.idNacionalidade,
    user?.usuario?.paciente?.idRacaCor,
    user?.usuario?.paciente?.nomeMae,
    user?.usuario?.paciente?.flagMaeDesconhecida,
    user?.usuario?.paciente?.nomeSocial,
    user?.usuario?.paciente?.cns,
    user?.usuario?.paciente?.municipioNascimento,
    user?.usuario?.paciente?.idUfNascimento,
    user?.usuario?.paciente?.flagAps,
    user?.usuario?.paciente?.titularidade,
    user?.usuario?.paciente?.pendenteAtualizacaoDadosCadastrais,
    user?.usuario?.flagAcessou,
    user?.usuario?.paciente?.menorDeIdade,
    {
      id: user?.usuario?.paciente?.titular?.id,
      name: user?.usuario?.paciente?.titular?.nome,
    },
    {
      socialWorker: user?.servicosClinica?.servicoAssistenteSocial,
      dentist: user?.servicosClinica?.servicoDentista,
      nursing: user?.servicosClinica?.servicoEnfermagem,
      physiotherapy: user?.servicosClinica?.servicoFisioterapeuta,
      speechTherapist: user?.servicosClinica?.servicoFonoaudiologo,
      medic: user?.servicosClinica?.servicoMedico,
      nutrition: user?.servicosClinica?.servicoNutricao,
      psychology: user?.servicosClinica?.servicoPsicologia,
      psyAlive: user?.servicosClinica?.servicoPsicologiaViva,
    },
    user?.usuario?.flagAtivo,
    user?.dependentesMaiorIdade,
    user?.dependentesMenorIdade,
    user?.termoUrl,
    user?.identificadorAtivacao,
    user?.flagPermiteEnvioBraze,
    user?.usuario?.flagTermoAceito,
    user?.usuario?.flagPrimeiroAcessoAposTermo,
    user?.usuario?.dataHoraTermoAceito,
    user?.usuario?.exibeLogin,
  )
}
